import { UrlInput } from "@inputs/url";
import InfoIcon from "@mui/icons-material/Info";
import { Divider, Stack, Typography } from "@mui/material";
import { Tooltip } from "@mui/material";
import { StackGrid } from "@styleguide";
import { AddressInput } from "./components/address";
import { AppellationInput } from "./components/appellations";
import { ApplyComponent } from "./components/apply";
import { ContactInput } from "./components/contact";
import { ContractInput } from "./components/contract";
import { ContractDurationInput } from "./components/contractDuration";
import { DateInput } from "./components/date";
import { DescriptionInput } from "./components/description";
import { EnterpriseInput } from "./components/enterprise";
import { OfferCreateInput } from "./components/offerCreate";
import { PermanentInput } from "./components/permanent";
import { SourceInput } from "./components/source";
import { WeeklyDurationInput } from "./components/weeklyDuration";
import { useModalOfferContext } from "./context";
import { ModalOfferStackGrid, classes } from "./styles";

interface IOfferFormProps {
  isApply?: boolean;
  disableEtp?: boolean;
}

export const OfferForm = ({ isApply, disableEtp }: IOfferFormProps) => {
  const { offer, setOffer, enterprise, searchUrlDone, setSearchUrlDone, getOfferUrl, cancelGetOfferUrl, loadingUrl } =
    useModalOfferContext();

  return (
    <ModalOfferStackGrid perLine={2} direction="row" columnGap={2} rowGap={2} pt={1}>
      {!offer.offerId && (
        <Stack className={classes.fullWidth}>
          <Typography variant="body1SemiBold" className={classes.fullWidth}>
            Vous avez trouvé une offre d&apos;emploi intéressante en dehors d&apos;Horizon ?
          </Typography>
          <Typography variant="body1" className={classes.fullWidth}>
            Ajoutez-la sur Horizon en renseignant les informations ci-dessous
          </Typography>
        </Stack>
      )}
      <UrlInput
        className={classes.fullWidth}
        url={offer.url ?? ""}
        onUrlChange={(url) => {
          setOffer((offer) => ({ ...offer, url }));
          if (!url) {
            setSearchUrlDone(false);
          }
        }}
        onSearch={getOfferUrl}
        onCancelSearch={cancelGetOfferUrl}
        loading={loadingUrl}
        searchDone={searchUrlDone}
        found={Boolean(offer.offerId)}
      />
      <Stack
        className={classes.fullWidth}
        sx={{
          flexDirection: "row",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle2SemiBold" color="primary">
          Détails du poste
        </Typography>
        <Tooltip
          title={
            <>
              Tapez le poste occupé, une appellation correspondante sera automatiquement détectée. N'hésitez pas à
              modifier cette suggestion si elle ne vous convient pas.
            </>
          }
        >
          <InfoIcon fontSize="small" color="primary" />
        </Tooltip>
      </Stack>
      <AppellationInput className={classes.fullWidth} />
      <AddressInput className={classes.fullWidth} />
      <ContractInput />
      {offer.contractType && !offer.contractType.includes("cdi") ? (
        <ContractDurationInput />
      ) : (
        <Stack
          sx={{
            flexDirection: "row",
            gap: 1,
            alignItems: "center",
          }}
        />
      )}
      <StackGrid perLine={2} columnGap={2} rowGap={2} className={classes.fullWidth}>
        <PermanentInput className={classes.fullWidth} />
      </StackGrid>
      <DateInput field="startDate" />
      <DateInput field="expirationDate" required={!offer.isPermanent} />
      <WeeklyDurationInput />
      <Stack
        sx={{
          flexDirection: "row",
          gap: 1,
          alignItems: "center",
        }}
      />
      <SourceInput isApply={isApply} />
      <DescriptionInput className={classes.fullWidth} />
      {((offer.offerId && !offer.enterprise?._id) ?? !offer.offerId) && (
        <>
          <Divider variant="fullWidth" className={classes.fullWidth} />
          {!offer.offerId && (
            <Typography variant="subtitle2SemiBold" color="primary">
              Entreprise
            </Typography>
          )}
          <EnterpriseInput isDisabled={disableEtp} className={classes.fullWidth} />
          {!enterprise?.name && (
            <Stack
              direction="row"
              spacing={3}
              sx={{
                justifyContent: "space-between",
              }}
            />
          )}
        </>
      )}
      <ContactInput className={classes.fullWidth} />
      {isApply && (
        <>
          <Divider variant="fullWidth" className={classes.fullWidth} />
          <Typography
            variant="subtitle2SemiBold"
            className={classes.fullWidth}
            sx={{
              color: "primary.dark",
            }}
          >
            Information sur la candidature
          </Typography>
          <OfferCreateInput className={classes.fullWidth} />
          <ApplyComponent />
        </>
      )}
    </ModalOfferStackGrid>
  );
};
